<template>
  <div
    :tabindex="searchable ? -1 : 0"
    class="multiselect"
    :class="{
      'multiselect--active': isOpen,
      'multiselect--disabled': disabled,
      'multiselect--above': !hasEnoughSpace,
    }"
    @focus="activate()"
    @blur="searchable ? false : deactivate()"
    @keydown.self.down.prevent="pointerForward()"
    @keydown.self.up.prevent="pointerBackward()"
    @keydown.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate()"
    v-click-outside="deactivate"
  >
    <slot name="carret">
      <div @mousedown.prevent="toggle()" class="multiselect__select"></div>
    </slot>
    <div ref="tags" class="multiselect__tags">
      <span
        v-for="option of visibleValue"
        @mousedown.prevent
        class="multiselect__tag"
        :class="[{ readonly: option.readonly }, option.className]"
      >
        <slot name="tag" :option="option">
          <span v-text="getOptionLabel(option)"></span>
        </slot>
        <i
          v-if="!option.readonly"
          aria-hidden="true"
          tabindex="1"
          @keydown.enter.prevent="removeElement(option)"
          @mousedown.prevent="removeElement(option)"
          class="multiselect__tag-icon"
        >
        </i>
      </span>
      <template v-if="internalValue && internalValue.length > limit">
        <strong v-text="limitText(internalValue.length - limit)"></strong>
      </template>
      <transition name="multiselect__loading">
        <slot name="loading">
          <div v-show="loading" class="multiselect__spinner"></div>
        </slot>
      </transition>
      <input
        ref="search"
        type="text"
        autocomplete="off"
        :placeholder="placeholder"
        v-if="searchable"
        :value="isOpen ? search : currentOptionLabel"
        :disabled="disabled"
        @input="updateSearch($event.target.value)"
        @focus.prevent="activate()"
        @blur.prevent="blur()"
        @keyup.esc="deactivate()"
        @keydown.down.prevent="pointerForward()"
        @keydown.up.prevent="pointerBackward()"
        @keydown.enter.prevent
        @keydown.enter.tab.stop.self="addPointerElement($event)"
        @keydown.delete="removeLastElement()"
        class="multiselect__input"
      />
      <span
        v-if="!searchable"
        class="multiselect__single"
        v-text="currentOptionLabel"
      >
      </span>
    </div>
    <transition name="multiselect">
      <div class="multiselect__content-wrapper">
        <ul
          :style="{ maxHeight: maxHeight + 'px' }"
          ref="list"
          v-show="isOpen"
          class="multiselect__content"
        >
          <slot name="beforeList"></slot>
          <li v-if="multiple && max === internalValue.length">
            <span class="multiselect__option">
              <slot name="maxElements">
                <span v-html="$t('profile.form.select_max_options', {max: maxChannels})"></span>
              </slot>
            </span>
          </li>
          <template v-if="!max || internalValue.length < max">
            <li
              class="multiselect__element"
              v-for="(option, index) of filteredOptions"
              :key="index"
            >
              <span
                tabindex="0"
                v-if="!option.$isLabel"
                :class="optionHighlight(index, option)"
                @mousedown.prevent="select(option)"
                @mouseenter="pointerSet(index)"
                :data-select="option.isTag ? tagPlaceholder : selectLabelText"
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                class="multiselect__option"
              >
                <slot name="option" :option="option" :search="search">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
              <span
                v-if="option.$isLabel"
                :class="optionHighlight(index, option)"
                class="multiselect__option multiselect__option--disabled"
              >
                {{ option.$groupLabel }}
              </span>
            </li>
          </template>
          <li v-show="filteredOptions.length === 0 && search && !loading">
            <span class="multiselect__option">
              <slot name="noResult">
                <span>{{$t("profile.form.select_no_results")}}</span>
              </slot>
            </span>
          </li>
          <slot name="afterList"></slot>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import multiselectMixin from "./multiselectMixin";
import pointerMixin from "./pointerMixin";
import vClickOutside from "v-click-outside";

export default {
  name: "multiselect",
  mixins: [multiselectMixin, pointerMixin],
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    /**
     * String to show when pointing to an option
     * @default 'Press enter to select'
     * @type {String}
     */
    selectLabel: {
      type: String,
      default: "Press enter to select",
    },
    /**
     * String to show next to selected option
     * @default 'Selected'
     * @type {String}
     */
    selectedLabel: {
      type: String,
      default: "Selected",
    },
    /**
     * String to show when pointing to an alredy selected option
     * @default 'Press enter to remove'
     * @type {String}
     */
    deselectLabel: {
      type: String,
      default: "Press enter to remove",
    },
    /**
     * Decide whether to show pointer labels
     * @default true
     * @type {Boolean}
     */
    showLabels: {
      type: Boolean,
      default: true,
    },
    /**
     * Limit the display of selected options. The rest will be hidden within the limitText string.
     * @default 'label'
     * @type {String}
     */
    limit: {
      type: Number,
      default: 99999,
    },
    /**
     * Sets maxHeight style value of the dropdown
     * @default 300
     * @type {Integer}
     */
    maxHeight: {
      type: Number,
      default: 300,
    },
    /**
     * Function that process the message shown when selected
     * elements pass the defined limit.
     * @default 'and * more'
     * @param {Int} count Number of elements more than limit
     * @type {Function}
     */
    limitText: {
      type: Function,
      default: (count) => `and ${count} more`,
    },
    /**
     * Set true to trigger the loading spinner.
     * @default False
     * @type {Boolean}
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Disables the multiselect if true.
     * @default false
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    visibleValue() {
      return this.multiple ? this.internalValue.slice(0, this.limit) : [];
    },
    deselectLabelText() {
      return this.showLabels ? this.deselectLabel : "";
    },
    selectLabelText() {
      return this.showLabels ? this.selectLabel : "";
    },
    selectedLabelText() {
      return this.showLabels ? this.selectedLabel : "";
    },
  },
  methods: {
    blur() {
      var val = this.search;
      this.search = "";
      this.$emit("blur", val, this.id);
    },
  },
};
</script>

<style lang="scss" scoped></style>
